import { Component, OnInit, signal, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ConfigService } from '../../services/config.service';
import { ThemeConfig, THEME_PRESETS, ThemeMode } from '../../models/config.model';
import { ButtonComponent } from '../ui';
import { ErrorService } from '../../shared/services/error.service';
import { TranslationService } from '../../shared/services/translation.service';

@Component({
  selector: 'app-settings',
  imports: [CommonModule, FormsModule, ButtonComponent],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.css'
})
export class SettingsComponent implements OnInit {
  currentTheme = signal<ThemeConfig | null>(null);
  themePresets = THEME_PRESETS;
  selectedPreset = signal<string>('custom');
  isCustomTheme = signal(false);
  currentMode = signal<ThemeMode>('light');

  // Color picker values
  color1 = signal('#2563eb');
  color2 = signal('#1e40af');
  color3 = signal('#3b82f6');
  color4 = signal('#10b981');
  color5 = signal('#f59e0b');
  
  // Border radius for input fields
  inputBorderRadius = signal('8px');

  private errorService = inject(ErrorService);
  private translationService = inject(TranslationService);

  constructor(public configService: ConfigService) {}

  ngOnInit(): void {
    const config = this.configService.getConfig();
    if (config?.theme) {
      this.currentTheme.set(config.theme);
      this.loadThemeValues(config.theme);
      this.checkPreset(config.theme);
    }
    
    // Get current mode from signal
    this.currentMode.set(this.configService.mode());
    
    // Watch for mode changes
    effect(() => {
      const mode = this.configService.mode();
      this.currentMode.set(mode);
    });
  }

  private loadThemeValues(theme: ThemeConfig): void {
    this.color1.set(theme.color1 || theme.primaryColor || '#2563eb');
    this.color2.set(theme.color2 || theme.secondaryColor || '#1e40af');
    this.color3.set(theme.color3 || '#3b82f6');
    this.color4.set(theme.color4 || '#10b981');
    this.color5.set(theme.color5 || '#f59e0b');
    this.inputBorderRadius.set(theme.inputBorderRadius || '8px');
  }

  private checkPreset(theme: ThemeConfig): void {
    const matchingPreset = this.themePresets.find(preset => {
      return preset.theme.color1 === theme.color1 &&
             preset.theme.color2 === theme.color2 &&
             preset.theme.color3 === theme.color3;
    });
    
    if (matchingPreset) {
      this.selectedPreset.set(matchingPreset.name);
      this.isCustomTheme.set(false);
    } else {
      this.selectedPreset.set('custom');
      this.isCustomTheme.set(true);
    }
  }

  selectPreset(presetName: string): void {
    const preset = this.themePresets.find(p => p.name === presetName);
    if (preset) {
      this.selectedPreset.set(presetName);
      this.isCustomTheme.set(false);
      // Load border radius from preset if available
      if (preset.theme.inputBorderRadius) {
        this.inputBorderRadius.set(preset.theme.inputBorderRadius);
      }
      this.applyTheme(preset.theme);
    } else if (presetName === 'custom') {
      this.selectedPreset.set('custom');
      this.isCustomTheme.set(true);
      this.applyCustomTheme();
    }
  }
  
  onBorderRadiusChange(value: string): void {
    this.inputBorderRadius.set(value);
    if (this.isCustomTheme()) {
      this.applyCustomTheme();
    } else {
      this.selectedPreset.set('custom');
      this.isCustomTheme.set(true);
    }
  }

  onColorChange(colorNumber: number, value: string): void {
    switch(colorNumber) {
      case 1: this.color1.set(value); break;
      case 2: this.color2.set(value); break;
      case 3: this.color3.set(value); break;
      case 4: this.color4.set(value); break;
      case 5: this.color5.set(value); break;
    }
    
    if (this.isCustomTheme()) {
      this.applyCustomTheme();
    } else {
      this.selectedPreset.set('custom');
      this.isCustomTheme.set(true);
    }
  }

  applyCustomTheme(): void {
    const customTheme: ThemeConfig = {
      color1: this.color1(),
      color2: this.color2(),
      color3: this.color3(),
      color4: this.color4(),
      color5: this.color5(),
      backgroundColor: '#ffffff',
      textColor: '#1f2937',
      buttonColor: this.color1(),
      buttonTextColor: '#ffffff',
      inputBorderRadius: this.inputBorderRadius(),
      primaryColor: this.color1(),
      secondaryColor: this.color2()
    };
    
    this.applyTheme(customTheme);
  }

  applyTheme(theme: ThemeConfig): void {
    this.currentTheme.set(theme);
    this.configService.updateTheme(theme);
    this.loadThemeValues(theme);
  }

  saveTheme(): void {
    const theme = this.currentTheme();
    if (theme) {
      this.configService.saveTheme(theme);
      this.errorService.showSuccess('Theme saved successfully!');
    }
  }

  resetTheme(): void {
    const defaultPreset = this.themePresets[0];
    if (defaultPreset) {
      this.selectPreset(defaultPreset.name);
      this.saveTheme();
    }
  }

  getColorLabel(index: number): string {
    const labels = ['Primary', 'Secondary', 'Accent', 'Success', 'Warning'];
    return labels[index - 1] || `Color ${index}`;
  }

  toggleMode(): void {
    this.configService.toggleMode();
  }

  setMode(mode: ThemeMode): void {
    this.configService.setMode(mode);
  }

  get currentLanguage(): string {
    return this.translationService.getCurrentLanguage();
  }

  getLanguageName(lang: string): string {
    const names: { [key: string]: string } = {
      'en': 'English',
      'mr': 'मराठी',
      'hi': 'हिंदी'
    };
    return names[lang] || lang.toUpperCase();
  }

  selectLanguage(lang: 'en' | 'mr' | 'hi'): void {
    this.translationService.setLanguage(lang);
    this.errorService.showSuccess('Language changed successfully!');
  }
}
